import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";
import { SignaturePad } from "../components/SignaturePad";
// Server now builds the signed-agreement HTML on its own; the client no
// longer needs to import buildAgreementHtml.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtGBP2 = (n) =>
  !n ? "£0.00" : `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicAgreement() {
  const { token } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | signed | error | locked
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sig1, setSig1] = useState("");
  const [sig2, setSig2] = useState("");
  const sig1Ref = useRef(null);
  const sig2Ref = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState(null);

  const loadAgreement = () => {
    setStatus("loading");
    setError("");
    axios
      .get(`${API}/service-agreements/public/${token}`)
      .then(({ data }) => {
        setAgreement(data);
        if (data.signed_at_dmy) setStatus("locked");
        else setStatus("ready");
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "This agreement link is invalid or has expired.");
        setStatus("error");
      });
  };

  useEffect(() => { loadAgreement(); /* eslint-disable-next-line */ }, [token]);

  const isJoint = Boolean(agreement && (agreement.joint_applicant_name || "").trim());
  const borrowed = agreement ? Math.max(Number(agreement.property_price || 0) - Number(agreement.deposit_amount || 0), 0) : 0;
  const feePct = agreement ? Number(agreement.service_fee_percent ?? 5) : 5;
  const fee = borrowed * (feePct / 100);

  const submit = async () => {
    setFailure(null);
    if (!confirmed) { setFailure({ message: "Please tick the confirmation above before signing." }); return; }
    const s1 = sig1 || sig1Ref.current?.toDataURL();
    const s2 = isJoint ? (sig2 || sig2Ref.current?.toDataURL()) : null;
    if (!s1 || s1.length < 1000) { setFailure({ message: "Please draw your signature in the box above." }); return; }
    if (isJoint && (!s2 || s2.length < 1000)) { setFailure({ message: "The joint applicant also needs to sign." }); return; }
    setSubmitting(true);
    try {
      // Server builds the HTML snapshot — we only post the signatures.
      const resp = await axios.post(`${API}/service-agreements/public/${token}/sign`, {
        confirmed: true,
        signature_customer_1: s1,
        signature_customer_2: s2 || null,
        signature_elin: null,
      });
      void resp;
      setStatus("signed");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : "The agreement couldn't be sent to Elin's Consultancy right now. Your signature is still here — please tap Retry.";
      setFailure({ message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") return <PageShell><p className="text-sage">Loading…</p></PageShell>;
  if (status === "error") return (
    <PageShell>
      <div data-testid="agreement-load-error" className="bg-white border border-red-200 rounded-xl p-8 text-center">
        <p className="font-serif text-xl text-forest mb-2">We couldn't open this agreement</p>
        <p className="text-sm text-sage mb-5">{error} Please check your connection or ask Elin's Consultancy for a fresh link.</p>
        <button
          type="button"
          onClick={loadAgreement}
          data-testid="agreement-load-retry"
          className="btn-gold"
        >
          Retry
        </button>
      </div>
    </PageShell>
  );
  if (status === "signed" || status === "locked") {
    return (
      <PageShell>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center" data-testid="agreement-signed-page">
          <CheckCircle2 className="mx-auto text-emerald-700 mb-3" size={36} />
          <h2 className="font-serif text-2xl text-forest mb-1">Agreement signed</h2>
          <p className="text-forest/70 text-sm">Thank you — Elin's Consultancy has received your signed Service Agreement. A copy has been filed to your record.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Toaster position="top-right" richColors />
      <AgreementBody a={agreement} borrowed={borrowed} feePct={feePct} fee={fee} isJoint={isJoint} />

      <div className="bg-white border border-forest/15 rounded-xl p-5 sm:p-7 mt-6 space-y-5" data-testid="agreement-public-sign-block">
        <h3 className="font-serif text-lg text-forest">9. Signatures</h3>
        <p className="text-[13.5px] text-forest/80">By signing, both parties confirm that they have read, understood and agree to this Agreement.</p>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="agreement-public-confirm"
            className="mt-1 w-4 h-4 accent-gold" />
          <span className="text-[14px] text-forest">
            I have read and understood this Service Agreement and I agree to it. I understand that by signing below I am entering into a legally binding agreement with Elin's Consultancy.
          </span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
              {isJoint ? "First applicant signature" : "Your signature"} — {agreement.customer_name}
            </p>
            <SignaturePad ref={sig1Ref} value={sig1} onEnd={setSig1} testid="agreement-public-sig-1" />
          </div>
          {isJoint && (
            <div>
              <p className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
                Second applicant signature — {agreement.joint_applicant_name}
              </p>
              <SignaturePad ref={sig2Ref} value={sig2} onEnd={setSig2} testid="agreement-public-sig-2" />
            </div>
          )}
        </div>

        {failure && (
          <div
            data-testid="agreement-public-error"
            className="bg-red-50 border border-red-200 rounded px-4 py-3 flex items-start gap-3"
          >
            <div className="flex-1">
              <p className="font-serif text-[14px] text-red-800">We couldn't send your signature</p>
              <p className="text-[12.5px] text-red-700 mt-1">{failure.message}</p>
              <p className="text-[11.5px] text-red-700/80 mt-1">Your signature is still here — tap Retry to try again.</p>
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="agreement-public-retry"
              className="btn-gold inline-flex items-center gap-1.5 !py-1.5 !px-3 disabled:opacity-50"
            >
              {submitting ? "Retrying…" : "Retry"}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={submitting || !confirmed}
          data-testid="agreement-public-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-50"
        >
          {submitting ? "Signing…" : "Sign and send to Elin's Consultancy"}
        </button>
      </div>
    </PageShell>
  );
}

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-cream">
    <header className="bg-forest text-cream">
      <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
        <div className="font-serif text-xl">Elin's Consultancy</div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-cream/70 inline-flex items-center gap-1.5"><Lock size={12} strokeWidth={1.8} /> Private link</div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-5 py-8 pb-16">
      {children}
    </main>
  </div>
);

const Row = ({ label, value }) => (
  <div className="py-2 border-b border-forest/8 last:border-b-0">
    <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/55">{label}</p>
    <p className="text-[13.5px] text-forest mt-0.5">{value || "—"}</p>
  </div>
);

const AgreementBody = ({ a, borrowed, feePct, fee, isJoint }) => (
  <div className="bg-white border border-forest/15 rounded-xl p-5 sm:p-7 space-y-5 shadow-sm" data-testid="agreement-public-body">
    <div className="border-b-2 border-forest pb-3">
      <h1 className="font-serif text-2xl text-forest">Service Agreement</h1>
      <p className="text-[12px] text-sage mt-1">Unit 7A2, West Road, London N17 0QT · info@elinsconsultancy.co.uk · 07435 058 311</p>
    </div>

    <div className="bg-cream-3 border border-forest/15 px-4 py-3 rounded">
      <p className="text-[11px] tracking-[0.18em] uppercase font-bold text-forest/70">Agreement date</p>
      <p className="text-forest">{a.agreement_date_dmy}</p>
    </div>

    <p className="text-[14px] text-forest/90">This Service Agreement ("Agreement") is made on the date above between <strong>Elin's Consultancy</strong>, at the address above ("Service Provider"), and:</p>

    <div className="bg-cream-3 border border-forest/15 rounded divide-y divide-forest/8 px-4">
      <Row label="Customer's full name or company name" value={isJoint ? `${a.customer_name} & ${a.joint_applicant_name}` : a.customer_name} />
      <Row label="Customer's full address" value={a.customer_address} />
      <Row label="Email" value={a.customer_email} />
      <Row label="Telephone" value={a.customer_phone} />
    </div>

    <section>
      <h3 className="font-serif text-lg text-forest">1. Services and timeframe</h3>
      <p className="text-[14px] text-forest/90 mt-2">Elin's Consultancy will provide the Services described below.</p>
      <div className="bg-cream-3 border border-forest/15 rounded px-4 py-3 mt-2">
        <p className="text-[11px] tracking-[0.18em] uppercase font-bold text-forest/70">Agreed services, deliverables and exclusions</p>
        <p className="text-forest mt-1 whitespace-pre-wrap">{a.services_description || "—"}</p>
      </div>
      <p className="text-[14px] text-forest/90 mt-3">The expected completion timeframe is 8 to 12 weeks, depending on the Customer's circumstances. This is an estimate, subject to information being provided and third-party processing times. Changes to the Services, fees or timeframe must be agreed by both parties in writing.</p>
    </section>

    <section>
      <h3 className="font-serif text-lg text-forest">2. Mortgage Offer and service fee</h3>
      <p className="text-[14px] text-forest/90 mt-2">An "Offer" means a formal written Mortgage Offer issued by a mortgage lender to the Customer in connection with the Services.</p>
      <p className="text-[14px] text-forest/90 mt-2">When the Customer receives the Mortgage Offer, the Customer must pay a service fee equal to <strong>{feePct}%</strong> of the amount borrowed after the deposit is deducted from the property purchase price. Interest and fees added to the mortgage are excluded from this calculation.</p>
      <p className="text-[14px] text-forest/90 mt-2">The service fee is payment for the Services and is not a deposit. Elin's Consultancy will issue an invoice showing the calculation. Payment is due as soon as possible upon receipt of the invoice following the Mortgage Offer.</p>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="bg-cream-3 border border-forest/15 rounded px-4 py-3">
          <p className="text-[11px] tracking-[0.18em] uppercase font-bold text-forest/70">Property purchase price (£)</p>
          <p className="text-forest mt-1">{a.property_price ? fmtGBP2(a.property_price) : "—"}</p>
        </div>
        <div className="bg-cream-3 border border-forest/15 rounded px-4 py-3">
          <p className="text-[11px] tracking-[0.18em] uppercase font-bold text-forest/70">Deposit amount (£)</p>
          <p className="text-forest mt-1">{a.deposit_amount ? fmtGBP2(a.deposit_amount) : "—"}</p>
        </div>
      </div>
      <div className="bg-gold-soft/30 border border-gold/40 px-4 py-3 rounded mt-3">
        <p className="font-bold text-forest">Mortgage borrowing: {fmtGBP2(borrowed)} · Service fee ({feePct}%): {fmtGBP2(fee)}</p>
      </div>
      <p className="text-[14px] text-forest/90 mt-2">Separate third-party charges must be disclosed to and approved by the Customer in writing before they are incurred.</p>
    </section>

    <StaticClause n="3. Customer responsibilities" body="The Customer will provide accurate information, documents, instructions and cooperation reasonably required to deliver the Services, respond to reasonable requests within the agreed timeframe and promptly notify Elin's Consultancy of changes affecting the Services." />

    <section>
      <h3 className="font-serif text-lg text-forest">4. Refunds</h3>
      <p className="text-[14px] text-forest/90 mt-2">If Elin's Consultancy is unable to complete the agreed Services because of a problem attributable to Elin's Consultancy, it will refund the service fee in full within 14 calendar days after confirming that it cannot complete the Services.</p>
      <p className="text-[14px] text-forest/90 mt-2">If Elin's Consultancy is ready and able to complete the agreed Services but cannot do so because of an act or omission by the Customer, the service fee will not be refunded, subject to applicable statutory rights and the requirement that any amount retained is fair and lawful.</p>
      <p className="text-[14px] text-forest/90 mt-2">Before relying on a Customer's act or omission under this clause, Elin's Consultancy will explain the issue in writing and allow the Customer 7 calendar days to resolve it, where it can reasonably be resolved.</p>
      <p className="text-[14px] text-forest/90 mt-2">If completion is prevented by circumstances attributable to neither party, the parties will agree in writing whether to extend the timeframe or end the Agreement, including how any fee already paid will be treated.</p>
    </section>

    <StaticClause n="5. Confidentiality" body="Each party will keep the other party's confidential information secure and use it only for this Agreement. Information may be disclosed where required by law or to persons who reasonably need it to perform the Services and are subject to confidentiality obligations." />
    <StaticClause n="6. Ending the Agreement" body="Either party may end this Agreement by written notice if the other party materially breaches it and fails to remedy the breach within 7 calendar days after receiving written notice describing it. Treatment of fees already paid is subject to Clause 4 and applicable statutory rights." />
    <StaticClause n="7. Governing law and disputes" body="This Agreement is governed by the laws of England and Wales. The parties will first seek to resolve disputes through discussion in good faith. If unresolved, either party may pursue remedies available under applicable law. Nothing removes mandatory consumer protections or any applicable right to bring proceedings in another UK jurisdiction." />
    <StaticClause n="8. Entire agreement and statutory rights" body="This Agreement, together with any attached written scope of services, records the agreement between the parties concerning the Services. Amendments must be agreed by both parties in writing. Nothing limits rights or remedies that cannot lawfully be excluded, including applicable statutory cancellation rights." />
  </div>
);

const StaticClause = ({ n, body }) => (
  <section>
    <h3 className="font-serif text-lg text-forest">{n}</h3>
    <p className="text-[14px] text-forest/90 mt-2">{body}</p>
  </section>
);
